/**
 * My Details Modal Component
 * Displays logged-in user details from access token
 * Removed: Token information, Session status, Auth level
 * Added: User role (ADMIN/STAFF/SUPER_ADMIN), Session expiry time
 */

import React from 'react';
import {
  View,
  Modal,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
} from 'react-native';
import { Text, Button } from '@app/design-system';
import { colors } from '@app/design-system';

interface UserDetailsModalProps {
  visible: boolean;
  onClose: () => void;
  userDetails: {
    userId: string;
    shopId: string;
    authLevel: 'Public' | 'Password';
    oauthToken?: string;
    publicAccessToken?: string;
    expiresAt?: number;
    userRole?: 'ADMIN' | 'STAFF' | 'SUPER_ADMIN' | 'PARTNER'; // Updated: Added PARTNER role
  } | null;
}

export const UserDetailsModal: React.FC<UserDetailsModalProps> = ({
  visible,
  onClose,
  userDetails,
}) => {
  if (!userDetails) {
    return null;
  }

  // Format expiry date as DD/MM/YYYY HH:MM:SS
  const formatExpiryDateTime = (timestamp?: number): string => {
    if (!timestamp) return 'N/A';

    const date = new Date(timestamp);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
  };

  // Determine user role - extract from authLevel or use provided role
  const getUserRole = (): string => {
    if (userDetails.userRole) {
      return userDetails.userRole;
    }
    // Fallback if role not provided
    return userDetails.authLevel === 'Password' ? 'ADMIN' : 'STAFF';
  };

  // Get role badge color
  const getRoleColor = (): string => {
    const role = getUserRole();
    switch (role) {
      case 'SUPER_ADMIN':
        return colors.error; // Red
      case 'PARTNER':
        return colors.success; // Green for partners
      case 'ADMIN':
        return colors.primary; // Primary color
      case 'STAFF':
      default:
        return colors.secondary; // Secondary color
    }
  };

  // Get role icon
  const getRoleIcon = (): string => {
    const role = getUserRole();
    switch (role) {
      case 'SUPER_ADMIN':
        return '👑';
      case 'PARTNER':
        return '🤝';
      case 'ADMIN':
        return '👨‍💼';
      case 'STAFF':
      default:
        return '👤';
    }
  };

  const expiryDateTime = formatExpiryDateTime(userDetails.expiresAt);
  const userRole = getUserRole();

  return (
    <Modal visible={visible} transparent animationType="slide" onRequestClose={onClose}>
      <View style={styles.overlay}>
        <View style={styles.container}>
          {/* Header */}
          <View style={styles.header}>
            <Text variant="h2" bold style={styles.headerTitle}>
              👤 My Details
            </Text>
            <TouchableOpacity onPress={onClose} style={styles.closeButton}>
              <Text style={styles.closeIcon}>✕</Text>
            </TouchableOpacity>
          </View>

          {/* Content */}
          <ScrollView style={styles.content} contentContainerStyle={styles.contentContainer}>
            {/* User Information Section */}
            <View style={styles.section}>
              <Text variant="h4" bold style={styles.sectionTitle}>
                👤 User Information
              </Text>

              <View style={styles.detailCard}>
                <View style={styles.detailRow}>
                  <Text variant="body" color="secondary" style={styles.detailLabel}>
                    User ID:
                  </Text>
                  <Text variant="body" bold style={styles.detailValue}>
                    {userDetails.userId}
                  </Text>
                </View>

                <View style={styles.divider} />

                <View style={styles.detailRow}>
                  <Text variant="body" color="secondary" style={styles.detailLabel}>
                    Shop ID:
                  </Text>
                  <Text variant="body" bold style={styles.detailValue}>
                    {userDetails.shopId}
                  </Text>
                </View>

                <View style={styles.divider} />

                {/* User Role (NEW) */}
                <View style={styles.detailRow}>
                  <Text variant="body" color="secondary" style={styles.detailLabel}>
                    User Role:
                  </Text>
                  <View style={styles.badgeContainer}>
                    <View
                      style={[
                        styles.badge,
                        { backgroundColor: getRoleColor() },
                      ]}
                    >
                      <Text
                        variant="caption"
                        bold
                        style={[
                          styles.badgeText,
                          {
                            color: getRoleColor() === colors.error ? colors.white : colors.gray9,
                          },
                        ]}
                      >
                        {getRoleIcon()} {userRole}
                      </Text>
                    </View>
                  </View>
                </View>
              </View>
            </View>

            {/* Session Expiry Section (NEW) */}
            <View style={styles.section}>
              <Text variant="h4" bold style={styles.sectionTitle}>
                ⏰ Session Expiry
              </Text>

              <View style={styles.expiryCard}>
                <View style={styles.expiryRow}>
                  <Text variant="body" color="secondary" style={styles.expiryLabel}>
                    Session Expires:
                  </Text>
                </View>

                <View style={styles.expiryValueContainer}>
                  <Text variant="body" bold style={styles.expiryValue}>
                    {expiryDateTime}
                  </Text>
                </View>

                <View style={styles.expiryNoteContainer}>
                  <Text variant="caption" color="secondary" style={styles.expiryNote}>
                    💡 After this time, you will need to log in again to continue accessing the admin portal.
                  </Text>
                </View>
              </View>
            </View>

          </ScrollView>

          {/* Footer Button */}
          <View style={styles.footer}>
            <Button onPress={onClose} style={styles.closeButtonLarge}>
              Close
            </Button>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  container: {
    backgroundColor: colors.white,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: '95%',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: colors.gray2,
  },
  headerTitle: {
    fontSize: 20,
    flex: 1,
  },
  closeButton: {
    padding: 8,
    borderRadius: 20,
    backgroundColor: colors.gray1,
  },
  closeIcon: {
    fontSize: 20,
    color: colors.gray9,
    fontWeight: 'bold',
  },
  content: {
    flex: 1,
    paddingHorizontal: 16,
  },
  contentContainer: {
    paddingVertical: 16,
  },
  section: {
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 16,
    marginBottom: 12,
    color: colors.gray9,
  },
  detailCard: {
    backgroundColor: colors.gray1,
    borderRadius: 12,
    paddingVertical: 12,
    paddingHorizontal: 12,
  },
  detailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 8,
  },
  detailLabel: {
    flex: 1,
    fontSize: 14,
  },
  detailValue: {
    flex: 1,
    textAlign: 'right',
    fontSize: 14,
    color: colors.primary,
  },
  divider: {
    height: 1,
    backgroundColor: colors.gray3,
    marginHorizontal: 8,
  },
  badgeContainer: {
    flex: 1,
    alignItems: 'flex-end',
  },
  badge: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
  },
  badgeText: {
    fontSize: 12,
  },
  // Session Expiry Styles (NEW)
  expiryCard: {
    backgroundColor: colors.gray1,
    borderRadius: 12,
    paddingVertical: 16,
    paddingHorizontal: 12,
    borderLeftWidth: 4,
    borderLeftColor: colors.primary,
  },
  expiryRow: {
    marginBottom: 12,
  },
  expiryLabel: {
    fontSize: 14,
    fontWeight: '500',
  },
  expiryValueContainer: {
    backgroundColor: colors.white,
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: 12,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: colors.gray3,
  },
  expiryValue: {
    fontSize: 16,
    fontFamily: 'monospace',
    color: colors.primary,
  },
  expiryNoteContainer: {
    marginTop: 8,
  },
  expiryNote: {
    fontSize: 12,
    lineHeight: 18,
    color: colors.gray7,
  },
  footer: {
    paddingHorizontal: 16,
    paddingVertical: 16,
    borderTopWidth: 1,
    borderTopColor: colors.gray2,
  },
  closeButtonLarge: {
    width: '100%',
  },
});

